import {
  BookmarkIcon,
  Brand,
  BrandJusIa,
  Chip,
  EarthIcon,
  FeedIcon,
  FileSearchIcon,
  HammerIcon,
  InformationIcon,
  LawyerDirectoryIcon,
  LogoutIcon,
  NewTabIcon,
  PuzzleIcon,
  QuestionAnswerIcon,
  QuestionIcon,
  SettingsIcon,
  UserAddIcon,
  UserMultiIcon,
} from '@farol-ds/react'
import { COMPONENT_NAME, SELECTED_OPTION } from '@jusbrasil-web/shared-ga4-events'

import { TopbarContextType } from '../../../types'
import { isManageUsersItemsSeen } from '../../../utils'
import { OnlineOfficeIcon } from '../../assets/online-office-icon'
import { JUSBRASIL_NEWS_GA4 } from '../ga4'
import {
  TopbarGA4Event,
  TopbarUserMenuCtx,
  TopbarUserMenuItem,
  TopbarUserMenuItemSymbol,
} from './types'

// Builds two paired GA4 events that we attach to menu items pointing to a
// landing page: one fires the click immediately and the other stores the
// component origin so the next page_view can attribute itself to this menu.
function buildGA4ClickAndOriginEvents(
  componentName: TopbarGA4Event['componentName'],
  selectedOption: TopbarGA4Event['selectedOption']
): TopbarGA4Event[] {
  return [
    { componentName, selectedOption },
    { componentName, selectedOption, method: 'store-component-for-next-page' },
  ]
}

export function getMenuItems(ctx: TopbarContextType, menuCtx: TopbarUserMenuCtx) {
  const { user, counters, currentUrl } = ctx
  const { baseUrl, userUrl } = menuCtx
  const logoutNextUrl = currentUrl ? `?next_url=${encodeURIComponent(currentUrl)}` : ''

  const isManageUsersItemsNotSeen = !isManageUsersItemsSeen(user)

  const typeformBaseUrl =
    'https://jusbrasilpesquisa.typeform.com/to/Si8TKsJU?utm_source=profile-menu'
  const typeformUrl = user?.email
    ? `${typeformBaseUrl}&email=${encodeURIComponent(user.email)}`
    : typeformBaseUrl

  return {
    pro: {
      title: 'Assine o Jusbrasil',
      clickEvents: ['Assinar Jusbrasil'],
      url: `${baseUrl}/pro?comp=topbar-menu-pro-link&cameFrom=topbar-menu-pro-link`,
      target: '_blank',
      leading: <Brand width={20} height={20} onlySymbol />,
    },
    'annual-plans': {
      title: 'Conheça os Planos Anuais',
      clickEvents: ['Conhecer Planos Anuais'],
      ga4Events: buildGA4ClickAndOriginEvents(
        COMPONENT_NAME.ANNUAL_PLANS_MENU_ITEM,
        SELECTED_OPTION.ANNUAL_PLANS
      ),
      url: `${baseUrl}/planos-anuais?fromComponent=topbar-menu-annual-plans-link`,
      target: '_blank',
      leading: <Brand width={20} height={20} onlySymbol />,
    },
    messages: {
      title: 'Mensagens',
      clickEvents: ['Mensagens'],
      url: `${baseUrl}/mensagens`,
      leading: <QuestionAnswerIcon />,
      count: counters?.messages,
    },
    notifications: {
      title: 'Notificações',
      clickEvents: ['Notificações'],
      url: `${baseUrl}/notificacoes`,
      leading: <EarthIcon />,
      count: counters?.notifications,
    },
    lawyers: {
      title: 'Encontre um advogado',
      clickEvents: ['Encontre um advogado'],
      url: `${baseUrl}/advogados-online/falar-agora`,
      leading: <LawyerDirectoryIcon />,
    },
    'manage-users': {
      title: 'Gerenciar usuários',
      clickEvents: ['Gerenciar usuários'],
      url: `${userUrl}/convite/gerenciar`,
      leading: <UserMultiIcon />,
      newItem: isManageUsersItemsNotSeen,
    },
    'buy-seats': {
      title: 'Contratar mais usuários',
      clickEvents: ['Contratar mais usuários'],
      url: `${baseUrl}/configuracoes?focus=subscription`,
      leading: <UserAddIcon />,
      newItem: isManageUsersItemsNotSeen,
    },
    jusbrasilia: {
      title: 'Jus IA',
      clickEvents: ['Jus IA'],
      ga4Events: buildGA4ClickAndOriginEvents(
        COMPONENT_NAME.JUSIA_MENU_ITEM,
        SELECTED_OPTION.JUS_IA
      ),
      url: `https://ia.jusbrasil.com.br/`,
      leading: <BrandJusIa height={20} width={20} onlySymbol />,
    },
    search: {
      title: 'Pesquisa jurídica',
      clickEvents: ['Pesquisa jurídica'],
      url: `${baseUrl}/iniciar-pesquisa?user-menu=true`,
      leading: <FileSearchIcon />,
    },
    lawsuits: {
      title: 'Processos',
      clickEvents: ['Processos'],
      url: `${baseUrl}/ir/processos`,
      leading: <HammerIcon />,
    },
    'jusbrasil-news': {
      title: 'Jusbrasil News',
      clickEvents: ['Jusbrasil News'],
      ga4Events: [
        {
          componentName: JUSBRASIL_NEWS_GA4.componentName,
          selectedOption: JUSBRASIL_NEWS_GA4.selectedOption,
        },
      ],
      url: typeformUrl,
      target: '_blank',
      leading: <NewTabIcon />,
      trailing: (
        <Chip size="sm" kind="positive">
          Beta
        </Chip>
      ),
    },
    'my-feed': {
      title: 'Meu Feed',
      clickEvents: ['Meu Feed'],
      url: `${baseUrl}/feed`,
      leading: <FeedIcon />,
    },
    'my-documents': {
      title: 'Documentos salvos',
      clickEvents: ['Documentos salvos'],
      url: `${baseUrl}/meus-documentos`,
      leading: <BookmarkIcon />,
    },
    'chrome-ext': {
      title: 'Extensão do Jusbrasil para Chrome',
      clickEvents: ['BrowserExtension.InstallExtensionClicked'],
      url: 'https://chrome.google.com/webstore/detail/jusbrasil/gnndbcenekaghpeaglkmmcgidogaejge',
      target: '_blank',
      leading: <PuzzleIcon />,
    },
    'online-office': {
      title: 'Escritório Online',
      clickEvents: ['Escritório Online'],
      url: 'https://escritorioonline.jusbrasil.com.br',
      target: '_blank',
      leading: <OnlineOfficeIcon />,
    },
    help: {
      title: 'Ajuda e suporte',
      clickEvents: ['Ajuda e suporte'],
      url: 'https://suporte.jusbrasil.com.br/hc/pt-br',
      target: '_blank',
      leading: <QuestionIcon />,
    },
    about: {
      title: 'Sobre',
      clickEvents: ['Sobre'],
      url: 'https://sobre.jusbrasil.com.br',
      leading: <InformationIcon />,
    },
    settings: {
      title: 'Configurações',
      clickEvents: ['Configurações'],
      url: `${baseUrl}/configuracoes`,
      leading: <SettingsIcon />,
    },
    logout: {
      title: 'Sair',
      clickEvents: ['Sair da conta'],
      url: `${baseUrl}/logout${logoutNextUrl}`,
      leading: <LogoutIcon />,
    },
  } satisfies Record<string, TopbarUserMenuItem>
}

export type TopbarUserMenuItemID = keyof ReturnType<typeof getMenuItems>
export type TopbarUserMenuItemKey = TopbarUserMenuItemID | TopbarUserMenuItemSymbol
